import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const initialState = {
  isLoading: false,
  isGenerating: false,
  error: null,
  currentDeck: null,
  cards: [],
  reviewQueue: [],
  reviewIndex: 0,
  isReviewLoading: false,
  hearts: { remaining: 15, max: 15, nextRefillAt: null, isUnlimited: false },
  collectibles: { progress: 0, threshold: 0, nextDino: null, justUnlocked: null },
};

const slice = createSlice({
  name: 'cards',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isGenerating = false;
      state.isReviewLoading = false;
      state.error = action.payload;
    },
    setCurrentDeck(state, action) {
      state.isLoading = false;
      state.currentDeck = action.payload;
    },
    setCards(state, action) {
      state.isLoading = false;
      state.cards = action.payload;
    },
    startGenerating(state) {
      state.isGenerating = true;
      state.error = null;
    },
    stopGenerating(state) {
      state.isGenerating = false;
    },
    upsertCard(state, action) {
      const card = action.payload;
      const i = state.cards.findIndex((c) => c.id === card.id);
      if (i === -1) state.cards.push(card);
      else state.cards[i] = card;
    },
    removeCard(state, action) {
      state.cards = state.cards.filter((c) => c.id !== action.payload);
    },
    startReviewLoading(state) {
      state.isReviewLoading = true;
      state.error = null;
    },
    setReviewQueue(state, action) {
      state.isReviewLoading = false;
      state.reviewQueue = action.payload.cards;
      state.reviewIndex = 0;
      state.hearts = action.payload.hearts;
      state.collectibles = action.payload.collectibles;
    },
    advanceReview(state) {
      state.reviewQueue = state.reviewQueue.slice(1);
    },
    setHearts(state, action) {
      state.hearts = action.payload;
    },
    setCollectibles(state, action) {
      state.collectibles = action.payload;
    },
  },
});

export default slice.reducer;

const API = process.env.REACT_APP_HOST_API;

// ----------------------------------------------------------------------

export function fetchDeck(deckId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${API}/decks/${deckId}`);
      dispatch(slice.actions.setCurrentDeck(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Decks/Get/InternalError'));
    }
  };
}

export function fetchCards(deckId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${API}/decks/${deckId}/cards`);
      dispatch(slice.actions.setCards(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Cards/List/InternalError'));
    }
  };
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Card generation now runs on the ai-generation worker queue — the POST just enqueues
// the job and returns the deck in generationStatus 'processing'. Poll until the worker
// flips it to 'idle' (done, fetch the new cards) or 'failed' (surface generationError).
const GENERATE_POLL_INTERVAL_MS = 2000;
const GENERATE_POLL_TIMEOUT_MS = 120000;

// File/photo imports (unlike pasted text) are parsed asynchronously by the ingestion worker
// queue, which only sets pendingImportText once it finishes — see ingestion.processor.js. A
// caller that fires generateCards right after importDeck resolves (e.g. Onboarding.js's
// handleUpload, deliberately not awaiting generation) can beat that worker, and the backend
// 422s with Cards/Generate/NoSourceText until it's ready. Retry on that specific error rather
// than surfacing it as a real failure.
const GENERATE_IMPORT_WAIT_TIMEOUT_MS = 60000;

async function postGenerate(deckId, cardMix) {
  const payload = cardMix ? { cardMix } : {};
  const deadline = Date.now() + GENERATE_IMPORT_WAIT_TIMEOUT_MS;
  for (;;) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await axios.post(`${API}/decks/${deckId}/generate`, payload);
    } catch (error) {
      const stillIngesting = error?.error?.message === 'Cards/Generate/NoSourceText';
      if (!stillIngesting || Date.now() >= deadline) throw error;
      // eslint-disable-next-line no-await-in-loop
      await wait(GENERATE_POLL_INTERVAL_MS);
    }
  }
}

export function generateCards(deckId, cardMix) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startGenerating());
    try {
      const response = await postGenerate(deckId, cardMix);
      let deck = response.data.data;

      const deadline = Date.now() + GENERATE_POLL_TIMEOUT_MS;
      while (deck?.generationStatus === 'processing' && Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await wait(GENERATE_POLL_INTERVAL_MS);
        // eslint-disable-next-line no-await-in-loop
        await dispatch(fetchDeck(deckId));
        deck = getState().cards.currentDeck;
      }

      if (deck?.generationStatus === 'failed') {
        const message = deck.generationError || 'Ai/CardGeneration/Failed';
        const generationFailedError = new Error(message);
        generationFailedError.error = { message };
        throw generationFailedError;
      }

      await dispatch(fetchCards(deckId));
      dispatch(slice.actions.stopGenerating());
      return deck;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Cards/Generate/InternalError'));
      throw error;
    }
  };
}

export function createCard(deckId, payload) {
  return async (dispatch) => {
    const response = await axios.post(`${API}/decks/${deckId}/cards`, payload);
    dispatch(slice.actions.upsertCard(response.data.data));
    await dispatch(fetchDeck(deckId));
    return response.data.data;
  };
}

export function updateCard(id, payload) {
  return async (dispatch) => {
    const response = await axios.patch(`${API}/cards/${id}`, payload);
    dispatch(slice.actions.upsertCard(response.data.data));
    return response.data.data;
  };
}

export function deleteCard(id, deckId) {
  return async (dispatch) => {
    await axios.delete(`${API}/cards/${id}`);
    dispatch(slice.actions.removeCard(id));
    await dispatch(fetchDeck(deckId));
  };
}

export function fetchReviewQueue(deckId, mode, limit) {
  return async (dispatch) => {
    dispatch(slice.actions.startReviewLoading());
    try {
      const response = await axios.get(`${API}/decks/${deckId}/review`, { params: { ...(mode && { mode }), ...(limit && { limit }) } });
      dispatch(slice.actions.setReviewQueue(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Cards/ReviewQueue/InternalError'));
    }
  };
}

// Records one wrong MCQ pick during the "guess until correct" retry flow — costs a heart but
// doesn't advance the queue or touch SRS scheduling (see submitWrongAttempt on the backend).
export function submitWrongAttempt(cardId, selectedChoice) {
  return async (dispatch) => {
    const response = await axios.post(`${API}/cards/${cardId}/attempt`, { selectedChoice });
    dispatch(slice.actions.setHearts(response.data.data.hearts));
    return response.data.data; // { hearts }
  };
}

// Pre-reveal AI correctness check for qa cards — no state mutation, since grading itself never
// touches hearts/collectibles (only the eventual submitReview outcome does).
export function gradeAnswer(cardId, typedAnswer) {
  return async () => {
    const response = await axios.post(`${API}/cards/${cardId}/grade`, { typedAnswer });
    return response.data.data; // { correct, canonicalAnswer }
  };
}

// rating is no longer chosen by the user — the server derives it from attemptsCount/timeSpentMs
// (see ratingFromPerformance in cards.controller.js).
export function submitReview(cardId, { selectedChoice, typedAnswer, attemptsCount, timeSpentMs }) {
  return async (dispatch) => {
    const response = await axios.post(`${API}/cards/${cardId}/review`, {
      selectedChoice, typedAnswer, attemptsCount, timeSpentMs,
    });
    dispatch(slice.actions.advanceReview());
    dispatch(slice.actions.setHearts(response.data.data.hearts));
    if (response.data.data.collectibles) dispatch(slice.actions.setCollectibles(response.data.data.collectibles));
    return response.data.data; // { card, xpEarned, wasCorrect, hearts, collectibles }
  };
}
